import { useEffect, useMemo, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";
import type { ISpeedControlParameterDto } from "@framework/types";

import type { IMultiAnalysisTimeRange } from "../../multi-analysis/types";
import { normalizeTrendRangeForRequest } from "../../multi-analysis/utils/trend-series";
import type { IChartData } from "../../scheme/components/analytics/interfaces";

interface IControlParameterDataRow {
  idTrain: number;
  Date: string;
  paramID: number;
  Value: number;
}

const EMPTY_PARAMETERS: ISpeedControlParameterDto[] = [];
const EMPTY_SERIES: IChartData[] = [];

export const getSpeedControlParameterKey = (parameter: ISpeedControlParameterDto): string =>
  `${parameter.kind}:${parameter.id}`;

export const useSpeedControlParameter = (trainId: number, globalRange: IMultiAnalysisTimeRange) => {
  const [parameterState, setParameterState] = useState<{
    trainId: number;
    parameters: ISpeedControlParameterDto[];
  } | null>(null);
  const [selection, setSelection] = useState<{ trainId: number; key: string } | null>(null);
  const [trendState, setTrendState] = useState<{ key: string; series: IChartData[] } | null>(null);
  const parameters = parameterState?.trainId === trainId ? parameterState.parameters : EMPTY_PARAMETERS;
  const selectedParameter =
    parameters.find(
      parameter => selection?.trainId === trainId && getSpeedControlParameterKey(parameter) === selection.key,
    ) ??
    parameters[0] ??
    null;
  const { start, end } = globalRange;
  const range = useMemo(() => normalizeTrendRangeForRequest({ start, end }), [start, end]);
  const requestKey =
    selectedParameter && range
      ? JSON.stringify([
          trainId,
          selectedParameter.kind,
          selectedParameter.id,
          selectedParameter.dataId,
          range.startTimestamp,
          range.endTimestamp,
        ])
      : null;

  const { fn: loadParameters } = useApiCall(
    (api, idTrain: number) =>
      api.fetchJson({
        url: `/trains/${idTrain}/speed-control-parameters`,
        method: "GET",
      }) as Promise<ISpeedControlParameterDto[]>,
    { success: false },
  );
  const { fn: loadTrend } = useApiCall(
    (api, args: { trainId: number; parameterId: number; startTimestamp: string; endTimestamp: string }) =>
      api.fetchJson({
        url: `/trains/${args.trainId}/speed-control-parameter-data`,
        method: "GET",
        data: { idxKey: args.parameterId, startTimestamp: args.startTimestamp, endTimestamp: args.endTimestamp },
      }) as Promise<IControlParameterDataRow[]>,
    { success: false },
  );
  const loadersRef = useRef({ loadParameters, loadTrend });
  useEffect(() => {
    loadersRef.current = { loadParameters, loadTrend };
  }, [loadParameters, loadTrend]);

  useEffect(() => {
    let cancelled = false;
    setParameterState(null);
    setSelection(null);
    setTrendState(null);
    void loadersRef.current
      .loadParameters(undefined, trainId)
      .then(result => {
        if (!cancelled) setParameterState({ trainId, parameters: result ?? [] });
      })
      .catch(() => {
        if (!cancelled) setParameterState({ trainId, parameters: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [trainId]);

  useEffect(() => {
    if (!selectedParameter || !range || !requestKey) return;
    let cancelled = false;
    void loadersRef.current
      .loadTrend(undefined, {
        trainId,
        parameterId: selectedParameter.dataId,
        ...range,
      })
      .then(rows => {
        if (cancelled) return;
        setTrendState({
          key: requestKey,
          series: [
            {
              idTrain: trainId,
              paramId: selectedParameter.dataId,
              name: selectedParameter.title,
              parameterName: selectedParameter.title,
              color: "#2f6ec8",
              units: selectedParameter.unitLabel ?? "",
              unitCode: selectedParameter.units,
              items: (rows ?? []).map(row => ({ date: row.Date, value: row.Value })),
            },
          ],
        });
      })
      .catch(() => {
        if (!cancelled) setTrendState({ key: requestKey, series: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [range, requestKey, selectedParameter, trainId]);

  return {
    parameters,
    selectedParameter,
    selectParameter: (key: string) => setSelection({ trainId, key }),
    trendSeries: requestKey && trendState?.key === requestKey ? trendState.series : EMPTY_SERIES,
    isLoadingParameters: parameterState?.trainId !== trainId,
    isLoadingTrend: requestKey !== null && trendState?.key !== requestKey,
  };
};
